import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Operation } from "../utils/operations";
import { Operations } from "../utils/operations";
import type { CalculationElement, VariableType } from "../types";
import type { NodeVariable } from "../../../types";
import { useCalculatedField } from "../hooks/useCalculatedField";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useGetNodeWithCalculatedFieldData } from "@/queries/flows/flows.api";
import {
  deserializeCalculatedInputWithConnections,
  serializeCalculationElements,
} from "../../serialization";

// Tipo extendido para inputs con datos de cálculo
interface CalculatedInput {
  id?: string;
  name: string;
  value: string | number | boolean | null;
  type: string;
  calculations?: Array<{
    id: string;
    calculation_node_input_id: string;
    operation: string;
    inputs: Array<{
      id: string;
      name: string;
      value: string | null;
      type: string;
      order?: number;
      source_connections?: Array<{
        id: string;
        field_id: string;
        field: {
          id: string;
          name: string;
          type: string;
          label: string;
          placeholder?: string;
          config?: Record<string, unknown>;
        };
      }>;
    }>;
  }>;
}

interface NodeInput {
  type: string;
}

// const [selectedVariable, setSelectedVariable] = useState<string>();
//   const [selectedOperation, setSelectedOperation] = useState<Operation>();
//   const [elements, setElements] = useState<CalculationElement[]>([]);
//   const [selectedDateGetFormat, setSelectedDateGetFormat] = useState<string>();
//   const [selectedDateToStringFormat, setSelectedDateToStringFormat] =
//     useState<string>();
//   const [selectedArrayCondition, setSelectedArrayCondition] =
//     useState<string>();
//   const [selectedArrayFunction, setSelectedArrayFunction] = useState<string>();

const CalculatedFieldContext = createContext<{
  variables: NodeVariable[];
  isLoadingVariables?: boolean;
  selectedVariable?: string;
  selectedOperation?: Operation;
  setSelectedOperation: (val?: Operation) => void;
  elements: CalculationElement[];
  setElements: (val: CalculationElement[]) => void;
  selectedDateGetFormat?: string;
  setSelectedDateGetFormat: (val: string) => void;
  selectedDateToStringFormat?: string;
  setSelectedDateToStringFormat: (val: string) => void;
  selectedArrayCondition?: string;
  setSelectedArrayCondition: (val: string) => void;
  selectedArrayFunction?: string;
  setSelectedArrayFunction: (val: string) => void;
  selectedVariableType?: VariableType;
  selectedVariableMetaType?: string;
  showDynamicElements?: boolean;
  hasMultipleValues?: boolean;
  addElement: () => void;
  removeElement: (id: string) => void;
  updateElement: (id: string, value: string) => void;
  handleVariableChange: (variableId: string) => void;
  setVariableForDeserialization: (variableId: string) => void;
  clearState: () => void;
  saveNode: (val: {
    display_name?: string;
    manual_continue?: boolean;
  }) => Promise<void>;
}>({
  variables: [],
  selectedVariable: undefined,
  selectedOperation: undefined,
  setSelectedOperation: () => {},
  elements: [],
  setElements: () => {},
  selectedDateGetFormat: "",
  setSelectedDateGetFormat: () => {},
  selectedDateToStringFormat: "",
  setSelectedDateToStringFormat: () => {},
  selectedArrayCondition: "",
  setSelectedArrayCondition: () => {},
  selectedArrayFunction: "",
  setSelectedArrayFunction: () => {},
  selectedVariableType: undefined,
  selectedVariableMetaType: undefined,
  showDynamicElements: false,
  hasMultipleValues: false,
  addElement: () => {},
  removeElement: () => {},
  updateElement: () => {},
  handleVariableChange: () => {},
  setVariableForDeserialization: () => {},
  clearState: () => {},
  saveNode: async () => {},
});

export const CalculatedFieldProvider = ({
  children,
  variables,
  isLoadingVariables,
}: {
  children: ReactNode;
  isLoadingVariables?: boolean;
  variables: NodeVariable[];
}) => {
  const { selectedNodeId, nodeDetailsOpen } = useUIStore();
  const { flowId } = useWorkflowStore();
  const { mutateAsync: updateNode } = useUpdateNode(flowId);
  const { mutateAsync: getNodeWithCalculatedData, isPending: isLoading } =
    useGetNodeWithCalculatedFieldData();

  const {
    selectedVariable,
    selectedArrayFunction,
    selectedOperation,
    elements,
    selectedDateGetFormat,
    selectedDateToStringFormat,
    selectedArrayCondition,
    selectedVariableType,
    selectedVariableMetaType,
    showDynamicElements,
    hasMultipleValues,
    setSelectedArrayFunction,
    setSelectedOperation,
    setSelectedDateGetFormat,
    setSelectedDateToStringFormat,
    setSelectedArrayCondition,
    setElements,
    addElement,
    removeElement,
    updateElement,
    handleVariableChange,
    setVariableForDeserialization,
    clearState,
  } = useCalculatedField(variables);

  // Estado para controlar si ya se ha intentado deserializar
  const [hasDeserialized, setHasDeserialized] = useState(false);

  // Deserialize existing data when component mounts
  useEffect(() => {
    if (!selectedNodeId || !nodeDetailsOpen || !flowId) return;
    // Esperar a que las variables estén cargadas antes de deserializar
    if (!variables || variables.length === 0) return;

    const loadNode = async () => {
      try {
        // Obtener el nodo con todos los datos necesarios para la deserialización
        const node = await getNodeWithCalculatedData({
          flowId,
          nodeId: selectedNodeId,
        });

        if (node && node.inputs) {
          // Buscar el input de tipo CALCULATED en la respuesta
          const calculatedInput = node.inputs.find(
            (input: NodeInput) => input.type === "CALCULATED",
          ) as CalculatedInput;

          if (
            calculatedInput &&
            calculatedInput.calculations &&
            calculatedInput.calculations.length > 0
          ) {
            const deserializedData = deserializeCalculatedInputWithConnections(
              calculatedInput as unknown as Record<string, unknown>,
              variables, // Pasar las variables para buscar la correcta
            );
            // Setear todos los datos deserializados
            if (deserializedData.selectedVariable) {
              setVariableForDeserialization(deserializedData.selectedVariable);
            }

            // Setear los elementos
            if (deserializedData.elements.length > 0) {
              setElements(deserializedData.elements);
            }

            // Buscar y setear la operación
            if (deserializedData.operation) {
              // Buscar la operación en la lista de operaciones disponibles según el tipo de variable
              // Buscar en todas las categorías de operaciones
              const allOperations = [
                ...Operations.string,
                ...Operations.number,
                ...Operations.date,
                ...Operations.boolean,
                ...Operations.array,
              ];

              const foundOperation = allOperations.find(
                (op) => op.name === deserializedData.operation,
              );

              if (foundOperation) {
                setSelectedOperation(foundOperation);
              } else {
                // Si no se encuentra, crear un objeto de operación básico
                setSelectedOperation({
                  name: deserializedData.operation,
                  label:
                    deserializedData.operation.charAt(0).toUpperCase() +
                    deserializedData.operation.slice(1),
                  needsValue: true,
                  hasMultipleValues: deserializedData.elements.length > 0,
                  helperText: "",
                  output: deserializedData.variableType as VariableType,
                });
              }

              // Setear configuraciones específicas según el tipo de operación
              if (deserializedData.variableType === "date") {
                // Para operaciones de fecha, setear formatos específicos si vienen en los datos
                if (
                  deserializedData.operation === "date_get_format" &&
                  deserializedData.selectedDateGetFormat
                ) {
                  setSelectedDateGetFormat(
                    deserializedData.selectedDateGetFormat,
                  );
                }

                if (
                  deserializedData.operation === "date_to_string" &&
                  deserializedData.selectedDateToStringFormat
                ) {
                  setSelectedDateToStringFormat(
                    deserializedData.selectedDateToStringFormat,
                  );
                }
              }

              if (deserializedData.variableType === "array") {
                // Para operaciones de array, podríamos necesitar setear condiciones específicas
                // Future implementation for array operation specific configurations
              }
            }
          }
        }
        // Marcar como deserializado aunque no haya datos
        setHasDeserialized(true);
      } catch (error) {
        console.error(
          "❌ [CalculatedFieldProvider] Error loading calculated field data:",
          error,
        );
        setHasDeserialized(true);
      }
    };

    loadNode();
  }, [
    selectedNodeId,
    nodeDetailsOpen,
    flowId,
    variables, // Agregar variables como dependencia para re-ejecutar cuando estén cargadas
    getNodeWithCalculatedData,
    setVariableForDeserialization,
    setElements,
    setSelectedOperation,
    setSelectedDateGetFormat,
    setSelectedDateToStringFormat,
  ]);

  // Resetear hasDeserialized cuando cambia el nodo
  useEffect(() => {
    setHasDeserialized(false);
  }, [selectedNodeId]);

  const saveNode = async (val: {
    display_name?: string;
    manual_continue?: boolean;
  }) => {
    if (!selectedNodeId || !selectedVariable || !selectedOperation) return;

    try {
      // Obtener el nodo actual con todos sus datos del backend
      const node = await getNodeWithCalculatedData({
        flowId,
        nodeId: selectedNodeId,
      });

      if (!node) {
        console.error("Node not found");
        return;
      }
      const calculatedInput = serializeCalculationElements(
        elements,
        selectedOperation,
        selectedVariableType || "string",
        selectedVariable,
        variables,
        selectedDateGetFormat,
        selectedDateToStringFormat,
      );

      await updateNode({
        node_id: selectedNodeId,
        node: {
          display_name: val.display_name || node.display_name,
          flow_id: flowId,
          order: node.order,
          position_x: node.position_x,
          position_y: node.position_y,
          ref_name: node.ref_name,
          type: node.type,
          manual_continue: val.manual_continue ?? false,
          inputs: [calculatedInput],
        },
      });
    } catch (error) {
      console.error("Error saving calculated field:", error);
    }
  };

  return (
    <CalculatedFieldContext.Provider
      value={{
        variables,
        selectedVariable,
        selectedOperation,
        setSelectedOperation,
        elements,
        setElements,
        selectedDateGetFormat,
        setSelectedDateGetFormat,
        selectedDateToStringFormat,
        setSelectedDateToStringFormat,
        selectedArrayCondition,
        setSelectedArrayCondition,
        selectedArrayFunction,
        setSelectedArrayFunction,
        selectedVariableType,
        selectedVariableMetaType,
        showDynamicElements,
        hasMultipleValues,
        addElement,
        removeElement,
        updateElement,
        handleVariableChange,
        setVariableForDeserialization,
        clearState,
        saveNode,
        isLoadingVariables: isLoadingVariables || isLoading || !hasDeserialized,
      }}
    >
      {children}
    </CalculatedFieldContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCalculatedFieldContext = () => {
  if (!CalculatedFieldContext) {
    throw new Error(
      "useCalculatedFieldContext must be used within a CalculatedFieldProvider.",
    );
  }
  return useContext(CalculatedFieldContext);
};
